import { useId, useState, type FormEvent } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { GlassDrawer, GlassDrawerFooter } from "../components/GlassDrawer";
import {
  sendMemberAuthInviteEmail,
  type MemberAuthInviteSendOutcome,
} from "../data/member-auth-invite";
import {
  provisionPlatformAccount,
  type ProvisionedPlatformAccount,
} from "../data/platform-cockpit";

const EMAIL_PATTERN = /^[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i;

type AuthDelivery = "not_requested" | "email_requested" | "already_registered" | "failed";

type ProvisionReceipt = {
  account: ProvisionedPlatformAccount;
  authDelivery: AuthDelivery;
};

function consoleRedirectOrigin(): string {
  if (typeof window === "undefined" || typeof window.location?.origin !== "string") {
    return "https://console.thesecops.app";
  }
  return window.location.origin;
}

function authDeliveryFromOutcome(
  outcome: MemberAuthInviteSendOutcome,
): Exclude<AuthDelivery, "not_requested"> {
  if (!outcome.ok) return "failed";
  return outcome.kind === "already_registered" ? "already_registered" : "email_requested";
}

export function PlatformProvisionDrawer({
  client,
  open,
  onClose,
  onProvisioned,
}: {
  client: SupabaseClient;
  open: boolean;
  onClose: () => void;
  onProvisioned: (account: ProvisionedPlatformAccount) => void;
}) {
  const formId = useId();
  const [name, setName] = useState("");
  const [firstAdminEmail, setFirstAdminEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<ProvisionReceipt | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailure, setCopyFailure] = useState(false);

  const trimmedName = name.trim();
  const trimmedEmail = firstAdminEmail.trim();
  const dirty = receipt === null && (trimmedName.length > 0 || trimmedEmail.length > 0);

  function resetAndClose(): void {
    setName("");
    setFirstAdminEmail("");
    setBusy(false);
    setFailure(null);
    setReceipt(null);
    setCopied(false);
    setCopyFailure(false);
    onClose();
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || receipt !== null) return;

    if (trimmedName.length === 0) {
      setFailure("Workspace name is required.");
      return;
    }
    if (trimmedName.length > 120) {
      setFailure("Workspace name must be 120 characters or fewer.");
      return;
    }
    if (trimmedEmail.length > 0 && !EMAIL_PATTERN.test(trimmedEmail)) {
      setFailure("That does not look like an email address. Fix it, or clear it to create without an invite.");
      return;
    }

    setBusy(true);
    setFailure(null);
    try {
      const outcome = await provisionPlatformAccount(
        client,
        trimmedName,
        trimmedEmail.length > 0 ? trimmedEmail : null,
      );
      if (!outcome.ok) {
        setFailure(outcome.reason);
        return;
      }

      let authDelivery: AuthDelivery = "not_requested";
      if (outcome.account.firstAdminInviteId !== null) {
        const authOutcome = await sendMemberAuthInviteEmail(
          client,
          outcome.account.accountId,
          outcome.account.firstAdminInviteId,
          consoleRedirectOrigin(),
        );
        authDelivery = authDeliveryFromOutcome(authOutcome);
      }

      setReceipt({ account: outcome.account, authDelivery });
      onProvisioned(outcome.account);
    } finally {
      setBusy(false);
    }
  }

  async function copyWorkspaceId(): Promise<void> {
    if (receipt === null) return;
    setCopyFailure(false);
    try {
      if (typeof navigator.clipboard?.writeText !== "function") {
        throw new Error("Clipboard unavailable");
      }
      await navigator.clipboard.writeText(receipt.account.accountId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopyFailure(true);
    }
  }

  return (
    <GlassDrawer
      open={open}
      onClose={resetAndClose}
      ariaLabel={receipt === null ? "Provision workspace" : "Workspace created"}
      busy={busy}
      dirty={dirty}
      size="lg"
    >
      {receipt === null ? (
        <form id={formId} className="platform-provision" aria-label="Provision workspace" onSubmit={(event) => void submit(event)}>
          <header className="platform-provision__head">
            <p className="platform-provision__eyebrow">Platform Accounts</p>
            <h2>Provision workspace</h2>
            <p>
              Creates one empty workspace and records you as the actor. Sites, checkpoints,
              routes, and schedules are set up later.
            </p>
          </header>

          <label className="tpl__field platform-provision__field">
            Workspace name
            <input
              aria-label="Workspace name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              autoComplete="organization"
              placeholder="Northgate Patrol Group"
              required
              autoFocus
              disabled={busy}
            />
            <span className="platform-provision__help">
              Shown to everyone in the workspace. It can be renamed later.
            </span>
          </label>

          <div className="platform-provision__generated" aria-label="Workspace id assigned on create">
            <span>
              Workspace id <small>generated</small>
            </span>
            <strong>Assigned on create</strong>
            <p>System UUID and immutable join key. You never type it.</p>
          </div>

          <label className="tpl__field platform-provision__field">
            <span>
              First admin email <small>optional</small>
            </span>
            <input
              aria-label="First admin email"
              type="email"
              value={firstAdminEmail}
              onChange={(event) => setFirstAdminEmail(event.target.value)}
              autoComplete="email"
              placeholder="name@company.example"
              disabled={busy}
            />
            <span className="platform-provision__help">
              Reserves a normal admin invite. Leave it blank to create an empty workspace.
            </span>
          </label>

          <aside className="platform-provision__boundary">
            <strong>What this does not do</strong>
            <p>No setup wizard, site, checkpoint, route, schedule, or billing action runs here.</p>
          </aside>

          {failure !== null ? (
            <p className="surface__state surface__state--error" role="alert">
              {failure}
            </p>
          ) : null}

          <p className="platform-provision__reason">
            {trimmedEmail.length > 0
              ? "Creates the workspace and reserves its first admin invite in one transaction."
              : "Creates the workspace without an admin invite."}
          </p>

          <GlassDrawerFooter>
            <button
              type="submit"
              form={formId}
              className="minter__confirm-go platform-provision__submit"
              disabled={busy}
            >
              {busy ? "Creating workspace..." : "Create workspace"}
            </button>
            <button type="button" className="minter__confirm-cancel" disabled={busy} onClick={resetAndClose}>
              Cancel
            </button>
          </GlassDrawerFooter>
        </form>
      ) : (
        <div className="platform-provision platform-provision--receipt">
          <header className="platform-provision__head">
            <p className="platform-provision__eyebrow">Platform Accounts</p>
            <h2>Workspace created</h2>
            <p>{receipt.account.accountName} is now selected in Accounts.</p>
          </header>

          <div className="platform-provision__proofs" aria-label="Provision proof">
            <span className="platform-provision__proof">Workspace created</span>
            {receipt.authDelivery === "email_requested" ? (
              <span className="platform-provision__proof">Admin sign-in email requested</span>
            ) : null}
            {receipt.authDelivery === "already_registered" ? (
              <span className="platform-provision__proof">Admin membership reserved</span>
            ) : null}
          </div>

          <section className="platform-provision__identity" aria-label="Created workspace identity">
            <strong>{receipt.account.accountName}</strong>
            <span>Workspace id</span>
            <code>{receipt.account.accountId}</code>
            <button type="button" className="pfd-copy" onClick={() => void copyWorkspaceId()}>
              {copied ? "Copied" : "Copy workspace id"}
            </button>
            {copyFailure ? (
              <p role="status">Copy is unavailable here. Select the workspace id to copy it.</p>
            ) : null}
          </section>

          {receipt.authDelivery === "failed" ? (
            <p className="platform-provision__mail-warning" role="alert">
              The workspace and pending admin invite were created, but the sign-in email could not
              be sent. Open this workspace in Admin, then Members, and use Resend sign-in email.
            </p>
          ) : null}

          {receipt.authDelivery === "not_requested" ? (
            <p className="platform-provision__empty-note">
              No first admin was invited. This workspace is intentionally empty.
            </p>
          ) : null}

          <GlassDrawerFooter>
            <button type="button" className="minter__confirm-go platform-provision__submit" onClick={resetAndClose}>
              Open workspace
            </button>
          </GlassDrawerFooter>
        </div>
      )}
    </GlassDrawer>
  );
}
